/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { setOffset } from '@/utils/pagination'
import { setAuthorizationConfig } from '@/utils/services'
import { setUrlWithParams } from '@/utils/url'
import axios from 'axios'
import { handleThrowError } from '@/utils/fetch'
import { aiMetadataBuildBatchesApiUrls } from '@/configs/api-urls/ai-metadata-build-batches'

const {
  base: apiUrlBase,
  buildAiMetadata,
  retryBuildAiMetadata,
  csvReport,
  csv
} = aiMetadataBuildBatchesApiUrls

export const registerAIMetadataBuildBatch = async (formData, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(
      apiUrlBase,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('registerAIMetadataBuildBatch', err) })
}

export const registerCSVAIMetadataBuildBatch = async (formData, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken, 'multipart/form-data')

  return axios
    .post(
      `${apiUrlBase}/${csv}`,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('registerCSVAIMetadataBuildBatch', err) })
}

export const getAIMetadataBuildBatches = async ({ searchParams = {} }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAIMetadataBuildBatches', err) })
}

export const getAIMetadataBuildBatch = async ({
  id
}, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(`${apiUrlBase}/${id}`)
  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAIMetadataBuildBatch', err) })
}

export const getAIMetadataBuildBatchItemsByBatchId = async ({ id, searchParams }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(
    `${apiUrlBase}/${id}/items`,
    { ...pagination, ...searchParams }
  )

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAIMetadataBuildBatchItemsByBatchId', err) })
}

export const getAIMetadataBuildBatchItemsAsCsv = async ({ id }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${id}/${csvReport}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAIMetadataBuildBatchItemsAsCsv', err) })
}

export const startAIMetadataBuildBatchProcessing = async ({ id }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${id}/${buildAiMetadata}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('startAIMetadataBuildBatchProcessing', err) })
}

export const retryAIMetadataBuildBatchProcessing = async ({ batchId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${batchId}/${retryBuildAiMetadata}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('startAIMetadataBuildBatchProcessing', err) })
}

export const retryAIMetadataBuildItemProcessing = async ({ batchId, itemId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(`${apiUrlBase}/${batchId}/${retryBuildAiMetadata}/${itemId}`, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('startAIMetadataBuildBatchProcessing', err) })
}
